import { useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { CheckCircle2, XCircle, Loader2, RotateCw } from 'lucide-react';
import { useGetFeedTeamsQuery } from '@/api/feed';
import { BatchItemResult } from '../QuestionCoachModal';

interface BatchResultsPanelProps {
  results: BatchItemResult[];
  leagueId?: string;
  onRetry: (key: string) => void;
}

/**
 * Results view for batch question creation (DKPLAT-501 Phase 1). Shows one row per
 * planned question with its success/failure state and a per-row Retry button that
 * only re-submits that item — succeeded rows are never re-submitted.
 */
export function BatchResultsPanel({ results, leagueId, onRetry }: BatchResultsPanelProps) {
  const { data: teams } = useGetFeedTeamsQuery(leagueId);

  const teamMap = useMemo(() => {
    const map = new Map<string, string>();
    teams?.forEach(team => map.set(team.externalId, team.name));
    return map;
  }, [teams]);

  const succeeded = results.filter(r => r.status === 'success').length;
  const failed = results.filter(r => r.status === 'error').length;
  const pending = results.filter(r => r.status === 'pending').length;

  return (
    <div className="space-y-6">
      <div>
        <h4 className="font-semibold mb-2">Creation Results</h4>
        <Separator className="mb-3" />
        <div className="flex gap-4 text-sm">
          <span className="text-green-600 font-medium">{succeeded} succeeded</span>
          {failed > 0 && <span className="text-destructive font-medium">{failed} failed</span>}
          {pending > 0 && <span className="text-muted-foreground">{pending} in progress</span>}
        </div>
      </div>

      {failed > 0 && (
        <Alert variant="destructive">
          <AlertDescription>
            {failed} question{failed === 1 ? '' : 's'} failed to create. Use Retry on each row, or
            "Retry Failed" below, to try again. Successfully created questions will not be
            re-submitted.
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {results.map((item, index) => (
          <Card
            key={item.key}
            className={`p-3 ${
              item.status === 'error'
                ? 'border-destructive bg-destructive/5'
                : item.status === 'success'
                  ? 'border-green-600/40 bg-green-600/5'
                  : ''
            }`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{index + 1}</Badge>
                <div>
                  <div className="text-sm font-medium">{item.fixtureDisplay}</div>
                  {item.teamId && (
                    <div className="text-xs text-muted-foreground">
                      {teamMap.get(item.teamId) || item.teamId}
                    </div>
                  )}
                  {item.playerName && (
                    <div className="text-xs text-muted-foreground">{item.playerName}</div>
                  )}
                  {item.teamIds && item.teamIds.length > 0 && (
                    <div className="text-xs text-muted-foreground">
                      {item.teamIds.map(id => teamMap.get(id) || id).join(' vs ')}
                    </div>
                  )}
                  {item.playerNames && item.playerNames.length > 0 && (
                    <div className="text-xs text-muted-foreground">
                      {item.playerNames.join(', ')}
                    </div>
                  )}
                  {item.status === 'error' && item.errorMessage && (
                    <div className="text-xs text-destructive mt-1">{item.errorMessage}</div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {item.status === 'success' && (
                  <CheckCircle2
                    className="h-5 w-5 text-green-600"
                    data-testid={`result-success-${item.key}`}
                  />
                )}
                {item.status === 'pending' && (
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                )}
                {item.status === 'error' && (
                  <>
                    <XCircle
                      className="h-5 w-5 text-destructive"
                      data-testid={`result-error-${item.key}`}
                    />
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onRetry(item.key)}
                          data-testid={`result-retry-${item.key}`}
                        >
                          <RotateCw className="h-3.5 w-3.5 mr-1" />
                          Retry
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        Retry only this question — successful rows are left untouched
                      </TooltipContent>
                    </Tooltip>
                  </>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
